import React, { useEffect, useState } from "react";
import logo from "../../images/logo.png";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

const OfficeEmployeeAgreement = () => {
  const [username, setUsername] = useState("");
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const [position, setPosition] = useState("");

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name, position } = res.data;
        setUsername(`${first_name} ${last_name}`);
        setPosition(position || "");
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const TASK_ID = 2; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "office_employee_agreement";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey]
          );
        }
      } catch (err) {
        console.error("Error checking agreement status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, effectiveUserId, isAdmin]);

  const handleCancel = () => {
    navigate(isAdmin ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Office Employee Agreement
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div
        className="form-wrapper my-4 px-4"
        style={{ fontSize: "15px", lineHeight: "1.8" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h3 className="m-0">Office Employee Agreement</h3>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        <div className="agreement-top" style={{ lineHeight: 1.8 }}>
          <div className="d-flex align-items-center mb-3">
            <strong className="me-2">THIS AGREEMENT made as of the</strong>
            <input
              type="text"
              className="form-control"
              style={{ width: "160px", marginRight: "10px" }}
              value={
                isSigned && signedDate
                  ? new Date(signedDate).toLocaleDateString()
                  : new Date().toLocaleDateString()
              }
              readOnly
            />
            <strong>BETWEEN:</strong>
          </div>

          <div className="text-center fw-bold mb-2">
            <span className="fw-normal">
              <strong>NEW-WAY TRUCKING LTD.</strong> a body corporate, with an
              office in the City of Acheson, in the Province of Alberta
              (hereinafter called the “Corporation”)
            </span>
          </div>

          <div className="text-center fw-bold mb-2">- and -</div>

          <div className="text-center fw-bold mb-2">
            <span className="fw-normal">
              <strong>{username || "__________________"} </strong>an individual
              resident in the City of Calgary/Edmonton, in the Province of
              Alberta (hereinafter called the "Employee")
            </span>
          </div>

          <p>
            <strong>WHEREAS</strong> the Corporation is engaged in the business
            of providing transportation services;
          </p>
          <p>
            <strong>AND WHEREAS</strong> the Employee has valuable skills and
            experience that will assist the Corporation in conducting its
            business;
          </p>
          <p>
            <strong>AND WHEREAS</strong>, the Corporation desires to be assured
            that Employee (i) will not compete with the Corporation for the
            period and within the geographical areas hereinafter specified and
            (ii) will not employ or disclose any of the Corporation’s
            proprietary or confidential information;
          </p>
          <p>
            <strong>NOW THEREFORE</strong> in consideration of the mutual
            covenants and agreements herein contained, the payment of one
            hundred dollars ($100.00) by the Corporation to the Employee, and
            for other good and valuable consideration, it is agreed by the
            parties hereto as follows:
          </p>
        </div>

        <div className="mt-5">
          <h5>
            <strong>1. DEFINITIONS</strong>
          </h5>
          <p className="ms-3">
            1.1 In this Agreement, the following terms shall have the following
            meanings:
          </p>
          <ul className="ms-5">
            <li>
              <strong>1.1.1 “Agreement”</strong> means this agreement;
            </li>
            <li>
              <strong>1.1.2 "Business"</strong> means the business of providing
              transportation services;
            </li>
            <li>
              <strong>1.1.3 “Effective Date”</strong> means{" "}
              <span style={{ textDecoration: "underline dotted" }}>
                .................................
              </span>{" "}
              ;
            </li>
            <li>
              <strong>1.1.4 “Geographic Area”</strong> means, the Province of
              Alberta, Canada;
            </li>
          </ul>

          <h5 className="mt-5">
            <strong>2. OFFICE AND DUTIES</strong>
          </h5>
          <p>
            2.1 Employee shall fill the position of{" "}
            <input
              type="text"
              className="form-control d-inline"
              style={{ width: "200px", display: "inline-block" }}
              value={position}
              onChange={(e) => {
                setPosition(e.target.value);
                setIsSigned(false); // require re-sign
              }}
              disabled={isSigned}
            />{" "}
            and shall have the duties normally ascribed to those positions and
            as assigned to him by the Corporation from time to time in its sole
            discretion. Employee shall act at all times to promote the
            Corporation’s Business and best interests. The Employee shall devote
            substantially all business time, labor, skill, undivided attention
            and best ability to the performance of his duties hereunder in a
            manner, which will faithfully and diligently further and expand the
            Business and interests of the Corporation.
          </p>

          <h5 className="mt-5">
            <strong>3. CONFIDENTIALITY</strong>
          </h5>
          <p>
            3.1 The Employee acknowledges that prior to the Effective Date he or
            she has and thereafter during the course of his or her continued
            employment hereunder with the Corporation he or she may, from time
            to time, become privy to confidential or proprietary information of
            the Corporation (the “Protected Information”), including trade
            secrets and confidential or proprietary information, knowledge,
            documents or materials owned, developed or possessed by the
            Corporation, whether in tangible or intangible form, which is not
            publicly disseminated information. Protected Information includes,
            but is not limited to, information pertaining to the Corporation’s
            business, assets, operations, research, analyses, projections,
            business relationships, including those with suppliers and others,
            products (including prices, costs, markets, sales or content),
            financial information or measures, business methods, future business
            plans, data bases, matters of a technical nature, including
            know-how, data, formulae, secret processes and designs, models,
            operating procedures, knowledge of the organization, and other
            information of any nature whatsoever owned, developed or possessed
            by the Corporation. The Employee hereby agrees to hold all Protected
            Information in confidence. The Employee also agrees that he or she
            will not except as required pursuant hereto or as otherwise required
            by law, publish, disclose or make use of any of the Protected
            Information. The foregoing covenant regarding Protected Information
            shall be effective during and after the term of this Agreement. The
            obligations of the Employee hereunder shall not apply to any
            information that is, or becomes, information in the public domain
            through no violation of this Agreement by the Employee.
          </p>
          <p>
            3.2 The Employee may not during the term hereof or thereafter make,
            use or permit to be used any notes, memoranda, electronic documents,
            documents, drawings or data, or copies thereof relating in any way
            to the business of the Corporation, other than in connection with
            the Employee's employment with the Corporation hereunder for the
            benefit of the Corporation. All such notes, memoranda, drawings,
            data, electronic documents, documents and copies thereof shall be
            and remain the sole property of the Corporation and, upon the
            termination of this Agreement for any reason, shall be left at or
            delivered to the Corporation’s registered office by the Employee.
          </p>

          <h5 className="mt-5">
            <strong>4. RESTRICTION AGAINST COMPETITION</strong>
          </h5>
          <p>
            4.1 The Employee will not, either while employed with the
            Corporation or for a period of twelve (12) months subsequent to the
            termination of the Employee’s employment with the Corporation, for
            any reason, anywhere in the Geographic Area unless acting for the
            Corporation or its affiliates or in accordance with the
            Corporation’s prior written consent, (directly or indirectly) own,
            manage, operate, join, control, finance or participate in the
            ownership, management, operation, control or financing of, or be
            connected as an officer, director, Employee, principal, agent,
            representative, consultant, investor, owner, partner, manager, joint
            venturer or otherwise with, or permit his or her name to be used by
            or in connection with, any business or enterprise engaged in
            competition with the Corporation.
          </p>
          <p>
            4.2 For the purpose of Section 4.1, a business is substantially
            similar to the Business if the business resembles (but is not
            necessarily identical to) the Business, sells, promotes or markets
            the same or similar products or services as the Business or
            resembles fundamental aspects of the Business, in one or more (but
            not necessarily all) material, relevant or significant respects,
            and/or has a general nature or character similar or related to that
            of the Business.
          </p>

          <h5 className="mt-5">
            <strong>5. RESTRICTION AGAINST SOLICITATION</strong>
          </h5>
          <p>
            5.1 The Employee will not, either while employed with the
            Corporation or for a period of twelve (12) months subsequent to the
            termination of the Employee’s employment with the Corporation, for
            any reason, without the Corporation’s express prior written consent,
            either as an individual, or in conjunction with any other person,
            firm, Corporation or other entity, whether acting as a principal,
            agent, Employee, consultant or in any other capacity whatsoever:
          </p>
          <ul className="ms-4">
            <li>
              <strong>5.1.1</strong> solicit, attempt to solicit, call upon or
              otherwise seek out, directly or indirectly, the business of any
              firm, person or Corporation who was a customer, client, supplier
              or distributor of the Corporation at the time the Employee ceased
              employment with the Corporation; or
            </li>
            <li>
              <strong>5.1.2</strong> solicit, attempt to solicit or communicate
              in any way with any Employees or consultants of the Corporation
              for the purpose of having such Employees or consultants employed
              or in any other way engaged by another person, firm, Corporation
              or other entity with whom the Employee may be affiliated or
              otherwise associated.
            </li>
          </ul>
          <p>
            5.2 The Employee agrees that the Corporation has a material interest
            in preserving the relationships the Business has developed with its
            customers against impairment by competitive activities. Accordingly,
            the Employee agrees that the restrictions and covenants in Sections
            3, 4, and 5 of this Agreement, and the Employee's execution of this
            Agreement, constitute a material inducement to the Corporation to
            enter into this Agreement, and to employ the Employee and that the
            Corporation would not enter into this Agreement absent the
            inducement.
          </p>

          <h5 className="mt-5">
            <strong>6. INJUNCTIVE RELIEF</strong>
          </h5>
          <p>
            6.1 The parties agree that the provisions of Section 3, 4, and 5 are
            essential and reasonable for the protection of the Corporation and,
            if breached, will result in irreparable harm to the Corporation.
            Without prejudice to any and all remedies which may be available to
            the Corporation at law or in equity, injunctive relief is the only
            effective relief for a breach of the covenants of the Employee under
            Sections 3, 4 and 5 and the Employee hereby agrees that the
            Corporation shall be entitled to injunctive relief, including an
            interim injunction, in any court of competent jurisdiction, to
            enforce any of the covenants of the Employee therein upon the breach
            or threatened breach thereof.
          </p>

          <h5>
            <strong>7. GENERAL</strong>
          </h5>
          <p>
            7.1 This Agreement shall be construed and enforced in accordance
            with, and the rights of the parties hereto shall be governed by, the
            laws of the Province of Alberta and the federal laws of Canada
            applicable therein. Each of the parties hereto hereby irrevocably
            attorns to the jurisdiction of the courts of the Province of
            Alberta.
          </p>

          <h5 className="mt-5">
            <strong>8. ENTIRE AGREEMENT</strong>
          </h5>
          <p>
            8.1 This Agreement constitutes the entire agreement between the
            parties hereto with respect to the employment of the Employee with
            the Corporation, and cancels and supersedes all prior agreements and
            understandings between the parties hereto with respect to the
            subject matter hereof. This Agreement may not be amended or modified
            in any way except by written instrument signed by the parties
            hereto.
          </p>

          <h5 className="mt-5">
            <strong>9. SEVERABILITY</strong>
          </h5>
          <p>
            9.1 In the event that any provisions of this Agreement shall be held
            by a court or another tribunal of competent jurisdiction to be
            unenforceable, such provision will be eliminated to the minimum
            extent necessary so that this Agreement shall otherwise remain in
            full force and effect.
          </p>

          <h5 className="mt-5">
            <strong>10. INDEPENDENT LEGAL ADVICE</strong>
          </h5>
          <p>
            10.1 The Employee acknowledges that the Employee has had the time
            and opportunity to obtain independent legal advice with respect to
            the execution of this Agreement, or has waived that opportunity, and
            that the Employee has read, understands and agrees with all of the
            terms and conditions contained in this Agreement.
          </p>

          <h5 className="mt-5">
            <strong>11. TIME</strong>
          </h5>
          <p>
            11.1 Time shall in all respects be of the essence of this Agreement.
          </p>

          <h5 className="mt-5">
            <strong>12. NO ASSIGNMENT</strong>
          </h5>
          <p>
            12.1 This Agreement is a personal services agreement and may not be
            assigned by either party without the prior written consent of the
            other party.
          </p>

          <h5 className="mt-5">
            <strong>13. NO WAIVERS</strong>
          </h5>
          <p>
            13.1 The waiver by either party of any breach of the terms of this
            Agreement shall not operate or be construed as a waiver by that
            party of any other breach of the same or any other term of this
            Agreement.
          </p>
          <h5 className="mt-5">
            <strong>14. SURVIVAL</strong>
          </h5>
          <p>
            14.1 Sections 5, 6, 7, 8 and 9 shall survive the termination of this
            Agreement.
          </p>

          <p className="mt-4">
            <strong>IN WITNESS WHEREOF</strong>, the parties have executed or
            caused to be executed this Agreement as of the date first above
            written.
          </p>
        </div>

        <hr className="my-5" />

        <form className="my-5">
          <div className="row mb-3">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="office_employee_agreement"
              taskId={2}
              userId={effectiveUserId}
              isAdmin={isAdmin}
              autoReloadOnSign={true} // ✅ same as BlueCross behaviour
              onSignComplete={async () => {
                try {
                  const token = localStorage.getItem("token");
                  await axios.put(
                    `${API_BASE}/api/users/${effectiveUserId}/position`,
                    { position: position.trim() },
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  // ❌ DO NOT reload. SignatureManager auto-refreshes UI already.
                } catch (err) {
                  console.error("❌ Failed to save position:", err);
                }
              }}
            />
            <ReviewManager
              taskName="office_employee_agreement"
              taskId={2}
              userId={effectiveUserId} // ✅ use same as SignatureManager
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default OfficeEmployeeAgreement;
